import type { AIModel, Job, JobMediaResponse } from '@volt/shared';

/** What a section can take as a reference/attachment. */
export type LibraryKind = 'image' | 'video' | 'audio';

export interface LibraryItem {
  /** Stable key: `<jobId>:<index of the result inside the job>`. */
  id: string;
  jobId: string;
  index: number;
  kind: LibraryKind;
  /** URL the grid can render directly (data: or provider CDN). */
  url: string;
  prompt: string;
  modelName: string;
  createdAt: string;
}

const KIND_BY_MODALITY: Partial<Record<AIModel['modality'], LibraryKind>> = {
  image: 'image',
  video: 'video',
  audio: 'audio',
  speech: 'audio',
};

const IMAGE_EXT = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'avif'];
const VIDEO_EXT = ['mp4', 'm4v', 'mov', 'webm'];
const AUDIO_EXT = ['mp3', 'wav', 'ogg', 'm4a'];

function resultUrls(job: Job): string[] {
  if (Array.isArray(job.resultUrls) && job.resultUrls.length > 0) return job.resultUrls;
  return job.resultUrl ? [job.resultUrl] : [];
}

/** Kind of a single result, judged by its own URL/mime (a job may mix none of these in practice, but 3D models must never leak in). */
export function kindOfUrl(url: string, fallback: LibraryKind): LibraryKind | null {
  if (url.startsWith('data:')) {
    const mime = url.slice(5, url.indexOf(';') > 0 ? url.indexOf(';') : url.indexOf(','));
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('video/')) return 'video';
    if (mime.startsWith('audio/')) return 'audio';
    return null;
  }
  const ext = url.split('?')[0]?.split('.').pop()?.toLowerCase() ?? '';
  if (IMAGE_EXT.includes(ext)) return 'image';
  if (VIDEO_EXT.includes(ext)) return 'video';
  if (AUDIO_EXT.includes(ext)) return 'audio';
  if (ext === 'glb' || ext === 'gltf' || ext === 'obj') return null;
  return fallback; // extension-less CDN links: trust the model's modality
}

/**
 * Finished generations of the user, flattened to one entry per result file,
 * newest first, limited to the kinds the current section can accept.
 */
export function buildLibraryItems(
  jobs: Job[],
  models: AIModel[],
  kinds: readonly LibraryKind[],
): LibraryItem[] {
  const modelsById = new Map(models.map((model) => [model.id, model]));
  const wanted = new Set(kinds);
  const items: LibraryItem[] = [];

  for (const job of jobs) {
    if (job.status !== 'done') continue;
    const model = modelsById.get(job.modelId);
    const modelKind = model ? KIND_BY_MODALITY[model.modality] : undefined;
    if (!modelKind) continue;
    const prompt = (job.input as { prompt?: unknown })?.prompt;

    resultUrls(job).forEach((url, index) => {
      const kind = kindOfUrl(url, modelKind);
      if (!kind || !wanted.has(kind)) return;
      items.push({
        id: `${job.id}:${index}`,
        jobId: job.id,
        index,
        kind,
        url,
        prompt: typeof prompt === 'string' ? prompt : '',
        modelName: model?.displayName ?? job.modelId,
        createdAt: job.createdAt,
      });
    });
  }
  return items.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** Which library kinds a `<input accept>`-style list of mime prefixes/extensions allows. */
export function kindsFromAccept(accept: readonly string[]): LibraryKind[] {
  const kinds = new Set<LibraryKind>();
  for (const entry of accept) {
    const value = entry.toLowerCase().replace(/^\./, '');
    if (value.startsWith('image/') || IMAGE_EXT.includes(value)) kinds.add('image');
    else if (value.startsWith('video/') || VIDEO_EXT.includes(value)) kinds.add('video');
    else if (value.startsWith('audio/') || AUDIO_EXT.includes(value)) kinds.add('audio');
  }
  return [...kinds];
}

/** Turns the API's base64 payload back into a regular `File`, so every existing upload path works unchanged. */
export function mediaResponseToFile(media: JobMediaResponse): File {
  const binary = atob(media.dataBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], media.fileName, { type: media.mime });
}
